/**
 * Structured frontend diagnostics.
 *
 * This is the only module allowed to write to the browser console. It has no
 * dependency on React, Recoil, the API client, or runtime configuration: the
 * application composition root pushes a validated runtime level in through
 * {@link configureLogging}.
 */

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const;

export type LogLevel = (typeof LOG_LEVELS)[number];
export type LogFields = Readonly<Record<string, unknown>>;
export type LogFieldsInput = LogFields | (() => LogFields);

export interface LogEntry extends Record<string, unknown> {
	level: LogLevel;
	msg: string;
	time: string;
	module?: string;
}

export type LogTransport = (entry: LogEntry) => void;

export interface Logger {
	debug(message: string, fields?: LogFieldsInput): void;
	info(message: string, fields?: LogFieldsInput): void;
	warn(message: string, fields?: LogFieldsInput): void;
	error(message: string, fields?: LogFieldsInput): void;
	enabled(level: LogLevel): boolean;
	child(name: string, context?: LogFields): Logger;
	withContext(context: LogFields): Logger;
}

export interface RuntimeLoggingResult {
	level: LogLevel;
	source: 'default' | 'runtime';
	valid: boolean;
}

export interface RequestContextInput {
	correlationId?: unknown;
	correlation_id?: unknown;
	requestId?: unknown;
	request_id?: unknown;
}

/** Never start with debug enabled before deployment runtime config is read. */
export const DEFAULT_LOG_LEVEL: LogLevel = 'info';

const SEVERITY: Readonly<Record<LogLevel, number>> = {
	debug: 10,
	info: 20,
	warn: 30,
	error: 40
};

const REDACTED = '<redacted>';
const MAX_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 40;
const MAX_STRING_LENGTH = 512;

const SENSITIVE_KEY_FRAGMENTS: readonly string[] = [
	'token',
	'password',
	'passwd',
	'passphrase',
	'secret',
	'credential',
	'apikey',
	'authorization',
	'authorisation',
	'cookie',
	'privatekey',
	'signingkey',
	'clientkey',
	'jwt',
	'bearer',
	'email',
	'phone'
];

const SENSITIVE_KEYS: ReadonlySet<string> = new Set([
	'auth',
	'code',
	'otp',
	'pin',
	'session',
	'sessionid',
	'sid',
	'body',
	'payload',
	'request',
	'response',
	'data',
	'content',
	'text',
	'address',
	'ip',
	'ipaddress'
]);

const SENSITIVE_HEADER_RE = /\b(authorization|proxy-authorization|cookie|set-cookie|x-api-key|api-key)\s*:\s*[^\r\n]*/gi;
const AUTH_VALUE_RE = /\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT_RE = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]*/g;
const OPAQUE_TOKEN_RE = /\b[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\b/g;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const SENSITIVE_QUERY_RE = /([?&])([A-Za-z0-9_.-]*(?:token|password|secret|code|key|auth|cookie|session)[A-Za-z0-9_.-]*)=([^&#\s]*)/gi;
const INLINE_SECRET_RE =
	/\b([A-Za-z0-9_.-]*(?:token|password|passwd|passphrase|secret|credential|api[_-]?key|authorization|authorisation|cookie|session|private[_-]?key|signing[_-]?key|client[_-]?key|jwt|bearer)[A-Za-z0-9_.-]*)\s*[:=]\s*("[^"]*"|'[^']*'|[^\s,;&]+)/gi;
const PRIVATE_KEY_RE = /-----BEGIN [^-\r\n]*PRIVATE KEY-----[\s\S]*?-----END [^-\r\n]*PRIVATE KEY-----/g;

let activeLevel: LogLevel = DEFAULT_LOG_LEVEL;
let activeSeverity = SEVERITY[DEFAULT_LOG_LEVEL];

/** Narrows deployment input to the four levels shared with the Go services. */
export function isLogLevel(value: unknown): value is LogLevel {
	return typeof value === 'string' && (LOG_LEVELS as readonly string[]).includes(value);
}

export function getLogLevel(): LogLevel {
	return activeLevel;
}

export function setLogLevel(level: LogLevel): void {
	activeLevel = level;
	activeSeverity = SEVERITY[level];
}

/**
 * Applies the value read from runtime config (normally NEXT_PUBLIC_LOG_LEVEL).
 * Missing values select the safe `info` default. Invalid values never enable
 * extra output and produce a redacted warning instead of breaking application boot.
 */
export function configureLogging(runtimeLevel: unknown): RuntimeLoggingResult {
	if (runtimeLevel === undefined || runtimeLevel === null || runtimeLevel === '') {
		setLogLevel(DEFAULT_LOG_LEVEL);
		return { level: activeLevel, source: 'default', valid: true };
	}

	if (!isLogLevel(runtimeLevel)) {
		setLogLevel(DEFAULT_LOG_LEVEL);
		log.warn('Ignoring invalid runtime log level', { requested_level: runtimeLevel });
		return { level: activeLevel, source: 'default', valid: false };
	}

	setLogLevel(runtimeLevel);
	return { level: activeLevel, source: 'runtime', valid: true };
}

export function isLevelEnabled(level: LogLevel): boolean {
	return SEVERITY[level] >= activeSeverity;
}

function normaliseKey(key: string): string {
	return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function isSensitiveLogKey(key: string): boolean {
	const normalised = normaliseKey(key);
	return SENSITIVE_KEYS.has(normalised) || SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalised.includes(fragment));
}

function safeObjectSize(value: object): number | null {
	try {
		return Object.keys(value).length;
	} catch {
		return null;
	}
}

/** A content-free description that remains useful in diagnostics. */
export function shapeOf(value: unknown): string {
	if (value === null) return 'null';
	if (Array.isArray(value)) return `array(${value.length})`;

	switch (typeof value) {
		case 'undefined':
			return 'undefined';
		case 'string':
			return `string(${value.length})`;
		case 'number':
		case 'bigint':
			return 'number';
		case 'boolean':
			return 'boolean';
		case 'function':
			return 'function';
		case 'symbol':
			return 'symbol';
		default:
			break;
	}

	if (value instanceof Map) return `map(${value.size})`;
	if (value instanceof Set) return `set(${value.size})`;
	if (value instanceof Date) return 'date';
	if (value instanceof Error) return 'error';

	const size = safeObjectSize(value);
	return size === null ? 'object' : `object(${size})`;
}

/** Prefer counts over logging collections or response objects. */
export function count(value: unknown): number {
	if (value === null || value === undefined) return 0;
	if (typeof value === 'string' || Array.isArray(value)) return value.length;
	if (value instanceof Map || value instanceof Set) return value.size;
	if (typeof value === 'object') return safeObjectSize(value) ?? 0;
	return 0;
}

/** Removes credential- and identity-shaped data from otherwise unstructured text. */
export function scrubLogText(text: string): string {
	return text
		.replace(PRIVATE_KEY_RE, '<redacted:private-key>')
		.replace(SENSITIVE_HEADER_RE, (_match, header: string) => `${header}: ${REDACTED}`)
		.replace(AUTH_VALUE_RE, (_match, scheme: string) => `${scheme} ${REDACTED}`)
		.replace(JWT_RE, '<redacted:jwt>')
		.replace(OPAQUE_TOKEN_RE, '<redacted:token>')
		.replace(SENSITIVE_QUERY_RE, (_match, separator: string, key: string) => `${separator}${key}=${REDACTED}`)
		.replace(INLINE_SECRET_RE, (_match, key: string) => `${key}=${REDACTED}`)
		.replace(EMAIL_RE, '<redacted:email>');
}

function scrubString(value: string): string {
	const scrubbed = scrubLogText(value);
	return scrubbed.length <= MAX_STRING_LENGTH
		? scrubbed
		: `${scrubbed.slice(0, MAX_STRING_LENGTH)}…<truncated:string(${scrubbed.length})>`;
}

function mask(value: unknown): unknown {
	return value === null || value === undefined ? value : `<redacted:${shapeOf(value)}>`;
}

function safeEntries(value: object): [string, unknown][] | null {
	try {
		return Object.entries(value);
	} catch {
		return null;
	}
}

function visit(value: unknown, depth: number, seen: WeakSet<object>): unknown {
	if (value === null || value === undefined) return value;

	switch (typeof value) {
		case 'string':
			return scrubString(value);
		case 'number':
		case 'boolean':
			return value;
		case 'bigint':
		case 'symbol':
			return String(value);
		case 'function':
			return '<function>';
		default:
			break;
	}

	const object = value;
	if (seen.has(object)) return '<circular>';
	if (value instanceof Date) return value.toISOString();
	if (value instanceof Error) return { name: value.name, message: scrubString(value.message) };
	if (typeof URL !== 'undefined' && value instanceof URL) return scrubString(value.href);
	if (depth > MAX_DEPTH) return `<${shapeOf(value)}>`;
	if (value instanceof Map || value instanceof Set) return `<${shapeOf(value)}>`;

	seen.add(object);
	try {
		if (Array.isArray(value)) {
			return value.length > MAX_ARRAY_ITEMS ? `<${shapeOf(value)}>` : value.map((item) => visit(item, depth + 1, seen));
		}

		const entries = safeEntries(object);
		if (entries === null) return '<unavailable:object>';
		if (entries.length > MAX_OBJECT_KEYS) return `<${shapeOf(value)}>`;

		const result: Record<string, unknown> = {};
		for (const [key, item] of entries) {
			result[key] = isSensitiveLogKey(key) ? mask(item) : visit(item, depth + 1, seen);
		}
		return result;
	} finally {
		seen.delete(object);
	}
}

/** Returns a recursively redacted copy and never mutates caller-owned data. */
export function redactLogFields(fields: LogFields): Record<string, unknown> {
	const entries = safeEntries(fields);
	if (entries === null) return { logging_error: '<unavailable:fields>' };

	const result: Record<string, unknown> = {};
	const seen = new WeakSet<object>();
	for (const [key, value] of entries) {
		result[key] = isSensitiveLogKey(key) ? mask(value) : visit(value, 1, seen);
	}
	return result;
}

function readProperty(object: Record<string, unknown>, key: string): unknown {
	try {
		return object[key];
	} catch {
		return undefined;
	}
}

function firstProperty(object: Record<string, unknown>, keys: readonly string[]): unknown {
	for (const key of keys) {
		const value = readProperty(object, key);
		if (value !== undefined && value !== null && value !== '') return value;
	}
	return undefined;
}

function safeIdentifier(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim() !== '' ? scrubString(value.trim()) : undefined;
}

/** Uses backend field names so one correlation-id search spans browser and services. */
export function requestContextFields(input: RequestContextInput): LogFields {
	const correlationId = safeIdentifier(input.correlationId ?? input.correlation_id);
	const requestId = safeIdentifier(input.requestId ?? input.request_id);
	return {
		...(correlationId === undefined ? {} : { correlation_id: correlationId }),
		...(requestId === undefined ? {} : { request_id: requestId })
	};
}

/**
 * Normalises unknown failures without serialising their response/body/stack.
 * API errors may expose camelCase or backend snake_case request identifiers.
 */
export function errorFields(error: unknown): LogFields {
	if (typeof error !== 'object' || error === null) {
		return { error: typeof error === 'string' ? scrubString(error) : String(error) };
	}

	const candidate = error as Record<string, unknown>;
	const result: Record<string, unknown> = {};
	const name = readProperty(candidate, 'name');
	const message = readProperty(candidate, 'message');
	const code = readProperty(candidate, 'code');
	const status = firstProperty(candidate, ['status', 'statusCode', 'status_code']);
	const correlationId = safeIdentifier(firstProperty(candidate, ['correlationId', 'correlation_id']));
	const requestId = safeIdentifier(firstProperty(candidate, ['requestId', 'request_id']));

	if (typeof name === 'string') result.error = scrubString(name);
	if (typeof message === 'string') result.error_message = scrubString(message);
	if (typeof code === 'string' || typeof code === 'number') result.error_code = code;

	const resolvedStatus = typeof status === 'number' ? status : typeof code === 'number' && code >= 100 && code <= 599 ? code : undefined;
	if (resolvedStatus !== undefined) result.status = resolvedStatus;
	if (correlationId !== undefined) result.correlation_id = correlationId;
	if (requestId !== undefined) result.request_id = requestId;
	if (Object.keys(result).length === 0) result.error = Array.isArray(error) ? 'array' : 'object';

	return result;
}

function consoleTransport(entry: LogEntry): void {
	const method = console[entry.level];
	method.call(console, entry);
}

let activeTransport: LogTransport = consoleTransport;

/** Installs a sink for already-redacted entries. The returned function restores the previous sink. */
export function setLogTransport(transport: LogTransport | null): () => void {
	const previous = activeTransport;
	activeTransport = transport ?? consoleTransport;
	return () => {
		activeTransport = previous;
	};
}

const RESERVED_ENTRY_KEYS: ReadonlySet<string> = new Set(['level', 'msg', 'time', 'module']);

function mergeFields(base: LogFields, extra?: LogFields): Record<string, unknown> {
	const result: Record<string, unknown> = {};
	const baseEntries = safeEntries(base);
	const extraEntries = extra === undefined ? [] : safeEntries(extra);

	if (baseEntries === null || extraEntries === null) {
		return { logging_error: '<unavailable:context>' };
	}

	for (const [key, value] of [...baseEntries, ...extraEntries]) result[key] = value;
	return result;
}

function evaluateFields(input?: LogFieldsInput): LogFields {
	if (input === undefined) return {};
	try {
		return typeof input === 'function' ? input() : input;
	} catch (error) {
		return { logging_fields_error: errorFields(error) };
	}
}

function write(level: LogLevel, module: string | undefined, context: LogFields, message: string, input?: LogFieldsInput) {
	const entry: LogEntry = {
		level,
		msg: scrubString(message),
		time: new Date().toISOString()
	};
	if (module !== undefined) entry.module = module;

	const fields = mergeFields(context, redactLogFields(evaluateFields(input)));
	for (const [key, value] of Object.entries(fields)) {
		entry[RESERVED_ENTRY_KEYS.has(key) ? `field_${key}` : key] = value;
	}
	activeTransport(entry);
}

function createLoggerWithSafeContext(module: string | undefined, stableContext: LogFields): Logger {
	return {
		debug(message, fields) {
			if (SEVERITY.debug >= activeSeverity) write('debug', module, stableContext, message, fields);
		},
		info(message, fields) {
			if (SEVERITY.info >= activeSeverity) write('info', module, stableContext, message, fields);
		},
		warn(message, fields) {
			if (SEVERITY.warn >= activeSeverity) write('warn', module, stableContext, message, fields);
		},
		error(message, fields) {
			if (SEVERITY.error >= activeSeverity) write('error', module, stableContext, message, fields);
		},
		enabled: isLevelEnabled,
		child(name, childContext = {}) {
			const childModule = module === undefined ? name : `${module}.${name}`;
			return createLoggerWithSafeContext(childModule, mergeFields(stableContext, redactLogFields(childContext)));
		},
		withContext(additionalContext) {
			return createLoggerWithSafeContext(module, mergeFields(stableContext, redactLogFields(additionalContext)));
		}
	};
}

export function createLogger(module?: string, context: LogFields = {}): Logger {
	return createLoggerWithSafeContext(module, redactLogFields(context));
}

/** Unnamespaced bootstrap logger. Feature code should create a named logger. */
export const log: Logger = createLogger();
